// Parsing the OAuth callback's URL fragment. The backend's provider callback
// (Google or Apple) redirects back to `/signin#token=…&identity=…&email=…&
// is_admin=…` on success, or `/signin#auth_error=<code>` on failure. The
// minted JWT rides in the FRAGMENT (never a query string) so it is not sent
// to any server or written to an access log; the SPA reads it client-side,
// persists the session, and scrubs the hash. GitHub is a per-project repo
// CONNECTION elsewhere in the product, never sign-in — no GitHub identity
// lands here.
//
// PURE (no `window`, no storage): the component injects the raw hash string, so
// this is unit-tested directly and ports unchanged.

import type { AuthResponse } from "@waytide/core";

/** The outcome of reading the callback fragment. */
export type AuthCallback =
  | { kind: "none" }
  | { kind: "session"; session: AuthResponse }
  | { kind: "error"; code: string };

/** A stable, human-readable message per backend `auth_error` code. Unknown codes
 * fall back to a generic line — we never echo a raw backend string. */
export function authErrorMessage(code: string): string {
  switch (code) {
    case "declined":
      return "Sign-in was cancelled.";
    case "no_verified_email":
      return "That account has no verified email. Verify one with the provider, then try again.";
    case "bad_state":
      return "That sign-in link expired. Please try again.";
    default:
      return "Sign-in didn't complete. Please try again.";
  }
}

/** Parse a location hash (`#token=…&…` or `#auth_error=…`, or anything else) into
 * an `AuthCallback`. A fragment carrying a non-empty `token` + `identity` becomes
 * a `session` (building the `AuthResponse` the token store persists, with the
 * email-derived display name); an `auth_error` becomes an `error`; anything else
 * is `none`. A `token` without an `identity` (malformed) is treated as `none` so a
 * half-written fragment never persists a broken session.
 *
 * `is_admin` is read as the string "1" (the backend stamps "1"/"0"); any other
 * value reads false. The email is REQUIRED to build the session (Signal's reach
 * keys on it); its absence downgrades to `none`. */
export function parseAuthCallback(hash: string): AuthCallback {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return { kind: "none" };
  const params = new URLSearchParams(raw);

  const error = params.get("auth_error");
  if (error) return { kind: "error", code: error };

  const token = params.get("token") ?? "";
  const identity = params.get("identity") ?? "";
  const email = params.get("email") ?? "";
  if (!token || !identity || !email) return { kind: "none" };

  const display_name = email.split("@")[0] || "user";
  const session: AuthResponse = {
    token,
    identity,
    user: { email, display_name },
    is_admin: params.get("is_admin") === "1",
  };
  return { kind: "session", session };
}
